import { useCallback, useState } from 'react';
import { SortingRule } from 'react-table';

import { OnTableStateChangedHandler } from './DataTable';

interface TableState<TData extends {}, TSortValue> {
  onChange: OnTableStateChangedHandler<TData>;
  sort?: TSortValue;
}

type SortDirection = 'asc' | 'desc';

type PartialRecord<K extends string, V> = {
  [P in K]?: V;
};

// Utility type to recursively flatten object keys using dot notation
type FlattenKeys<
  T,
  Prefix extends string = '',
  Depth extends number[] = [],
> = Depth['length'] extends 10 // Prevent infinite recursion (adjust as needed)
  ? never
  : {
      [K in keyof T & string]: T[K] extends Record<string, unknown>
        ? FlattenKeys<T[K], `${Prefix}${K}.`, [...Depth, 1]>
        : `${Prefix}${K}`;
    }[keyof T & string];

export type SortConfig<TData extends {}, TSortValue = string> = Partial<{
  [K in FlattenKeys<TData>]: PartialRecord<SortDirection, TSortValue>;
}>;

interface Config<TData extends {}, TSortValue> {
  sort: SortConfig<TData, TSortValue>;
}

const resolveSort = <TData extends {}, TSortValue>(
  sortState: SortingRule<TData>[] | undefined,
  config?: SortConfig<TData, TSortValue>
): TSortValue | undefined => {
  if (!sortState || sortState.length === 0) return undefined;

  const sortDirection: SortDirection = sortState[0].desc ? 'desc' : 'asc';
  const sortKey = sortState[0].id as FlattenKeys<TData>; // Ensure correct key type

  if (!config) {
    return undefined;
  }

  const sort = config?.[sortKey]?.[sortDirection];

  // If a direct match is found, return it
  if (sort) {
    return sort;
  }

  // Otherwise, find the closest match in the config
  const result = Object.keys(config).find(k => k.includes(`.${sortKey}`)) as
    | keyof SortConfig<TData, TSortValue>
    | undefined;

  return result ? config[result]?.[sortDirection] : undefined;
};

/**
 * A hook for declaratively handling table change states and resolving sort values
 */
export const useTableState = <TData extends {}, TSortValue = string>(
  config: Config<TData, TSortValue>
): TableState<TData, TSortValue> => {
  const [sortState, setSortState] = useState<SortingRule<TData>[]>();

  const onChange = useCallback<OnTableStateChangedHandler<TData>>(({ sortBy }) => {
    setSortState(sortBy);
  }, []);

  const sort = resolveSort<TData, TSortValue>(sortState, config.sort);

  return {
    onChange,
    sort,
  };
};
